import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "../api";
import {
  Briefcase, FileText, RefreshCw, Star, UserCheck, Upload, X, Image as ImageIcon,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const KPIS = [
  { key: "total_openings",     label: "Total Job Openings",  icon: Briefcase, color: "#22c55e", bg: "#22c55e1a" },
  { key: "total_applications", label: "Total Applications",  icon: FileText,  color: "#0ea5e9", bg: "#0ea5e91a" },
  { key: "in_process",         label: "In Process",          icon: RefreshCw, color: "#a855f7", bg: "#a855f71a" },
  { key: "offered",            label: "Offered",             icon: Star,      color: "#f59e0b", bg: "#f59e0b1a" },
  { key: "joined",             label: "Joined",              icon: UserCheck, color: "#eab308", bg: "#eab3081a" },
];

function CompanyLogoUploader({ initialUrl, name, onChange }) {
  const [url, setUrl] = useState(initialUrl || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) { setErr("Logo too large (max 4 MB)"); return; }
    setErr(""); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/me/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(r.data.company_logo);
      onChange?.(r.data.company_logo);
    } catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try { await api.delete("/me/company/logo"); setUrl(""); onChange?.(""); }
    catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-3" data-testid="company-logo-uploader">
      <div
        className="w-14 h-14 rounded-2xl bg-white border grid place-items-center overflow-hidden"
        style={{ borderColor: "var(--rule)" }}
      >
        {url ? (
          <img src={`${API_BASE}${url}`} alt="Company logo" className="w-full h-full object-contain p-1.5" />
        ) : (
          <ImageIcon size={18} className="text-muted" />
        )}
      </div>
      <div className="min-w-0">
        <div className="font-display font-extrabold uppercase text-sm tracking-widest truncate max-w-[180px]" data-testid="company-name">
          {name || "YOUR COMPANY"}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" ref={fileRef} onChange={pick} className="hidden" data-testid="company-logo-file" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="text-[11px] font-semibold text-signal hover:underline disabled:opacity-50" data-testid="company-logo-upload-btn">
            <Upload size={11} className="inline mr-1" />{url ? "Replace logo" : "Upload logo"}
          </button>
          {url && (
            <button type="button" onClick={clear} disabled={busy} className="text-[11px] text-red-600 hover:underline disabled:opacity-50" data-testid="company-logo-clear-btn">
              <X size={11} className="inline mr-0.5" />Remove
            </button>
          )}
        </div>
        {err && <div className="text-[10px] text-red-600 mt-0.5" data-testid="company-logo-error">{err}</div>}
      </div>
    </div>
  );
}

function KpiTile({ tile, value }) {
  const { icon: Icon, label, color, bg } = tile;
  return (
    <div className="card p-5 flex items-center gap-4 hover:shadow-lg transition-shadow" data-testid={`kpi-${tile.key}`}>
      <div className="w-16 h-16 rounded-xl grid place-items-center shrink-0" style={{ background: bg, color }}>
        <Icon size={28} />
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-muted">{label}</div>
        <div className="font-display font-black text-4xl mt-1" style={{ color }}>{value ?? 0}</div>
      </div>
    </div>
  );
}

function NeedToHireCard({ row }) {
  const pct = Math.min(100, (row.active_candidates / Math.max(1, row.openings * 3)) * 100);
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border hover:bg-paper transition-colors" style={{ borderColor: "var(--rule)" }} data-testid={`need-hire-${row.id}`}>
      <div className="relative w-14 h-14 shrink-0">
        <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
          <circle cx="18" cy="18" r="14" fill="none" stroke="var(--rule)" strokeWidth="3" />
          <circle cx="18" cy="18" r="14" fill="none" stroke="#006CFF" strokeWidth="3" strokeDasharray={`${pct * 0.88} 999`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 grid place-items-center font-display font-black text-signal">{row.openings}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm truncate">{row.title}</div>
        <div className="text-xs text-muted mt-0.5">Active: <b className="text-default">{row.active_candidates}</b> Candidates</div>
      </div>
    </div>
  );
}

export default function RecruitmentDashboard({ user }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [logo, setLogo] = useState(user?.company_logo || "");

  useEffect(() => {
    api.get("/recruitment/summary").then((r) => setData(r.data)).catch((e) => setErr(formatError(e)));
    api.get("/auth/me").then((r) => setLogo(r.data?.company_logo || "")).catch(() => {});
  }, []);

  if (err) return <div className="card card-pad text-red-600" data-testid="recruitment-summary-error">{err}</div>;

  const kpis = data?.kpis || {};
  const need = data?.need_to_hire || [];
  const tasks = data?.my_tasks || [];

  return (
    <div className="space-y-6" data-testid="recruitment-dashboard">
      <div className="card flex flex-wrap items-center justify-between gap-4 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="eyebrow text-signal">Recruitment</span>
          <span className="text-muted text-sm hidden md:inline">·</span>
          <CompanyLogoUploader
            initialUrl={logo}
            name={(user?.organization || user?.name || "Your Company").toUpperCase()}
            onChange={setLogo}
          />
        </div>
        <button className="btn-ghost" onClick={() => window.location.reload()} data-testid="recruitment-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <h2 className="font-display font-black text-2xl">Recruitment Summary</h2>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {KPIS.map((tile) => <KpiTile key={tile.key} tile={tile} value={kpis[tile.key]} />)}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card card-pad">
          <div className="flex items-center justify-between mb-3">
            <span className="eyebrow">You need to hire</span>
            <a href="/company/dashboard/postings" className="text-xs text-signal hover:underline">View all →</a>
          </div>
          <div className="space-y-2" data-testid="need-to-hire-list">
            {need.length ? need.map((r) => <NeedToHireCard key={r.id} row={r} />)
                          : <div className="text-sm text-muted">No active openings — post a job to start hiring.</div>}
          </div>
        </div>

        <div className="card card-pad">
          <div className="flex items-center justify-between mb-3">
            <span className="eyebrow">My tasks</span>
            <span className="text-xs text-muted">{tasks.length} new task{tasks.length === 1 ? "" : "s"}</span>
          </div>
          <ol className="space-y-3 max-h-[420px] overflow-y-auto" data-testid="my-tasks">
            {tasks.length ? tasks.map((t, i) => (
              <li key={`${t.student_name}-${i}`} className="flex items-start gap-3 pb-3 border-b last:border-b-0" style={{ borderColor: "var(--rule)" }}>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-red-100 text-red-600 shrink-0">{t.status}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{t.student_name}{t.posting_title ? <span className="text-muted"> — {t.posting_title}</span> : null}</div>
                  <div className="text-xs text-muted">Interview {t.scheduled_at ? `on ${String(t.scheduled_at).slice(0, 10)}` : "(unscheduled)"}</div>
                </div>
              </li>
            )) : <div className="text-sm text-muted">No pending interview tasks.</div>}
          </ol>
        </div>
      </div>
    </div>
  );
}
